import React, { useEffect } from 'react';
import cn from 'classnames';
import { Modal, ModalProps } from 'react-responsive-modal';
// Components
import Button, { ButtonVariant, ButtonIcon } from 'ui3/Button/Button';

import 'react-responsive-modal/styles.css';
import styles from './NotificationModal.module.scss';

const notificationTypeIcons: {
  [k in NotificationModalProps['type']]: string;
} = {
  success: '/images/notification-modal/success.svg',
  error: '/images/notification-modal/error.svg',
  confirm: '/images/notification-modal/confirm3d.svg',
};

export type NotificationModalTextContent = {
  highlightedTitle?: string;
  title: string;
  description?: string | React.ReactElement;
} | null;

type NotificationModalProps = {
  open: boolean;
  type: 'success' | 'error' | 'confirm';
  onClose: () => void;
  loading?: boolean;
  acceptLabel?: string;
  acceptButtonColor?: ButtonVariant;
  acceptButtonIcon?: ButtonIcon;
  acceptDisabled?: boolean;
  onAcceptClick?: () => void;
  withCancelButton?: boolean;
  onCancelClick?: () => void;
  cancelLabel?: string;
  cancelButtonColor?: ButtonVariant;
  cancelButtonIsDisabled?: boolean;
  withoutTitleImage?: boolean;
  modalFooter?: React.ReactElement;
  /** Stream-ending variant: narrower modal, darker overlay, wider action buttons */
  isStreamEnding?: boolean;
} & NotificationModalTextContent;

const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  type,
  highlightedTitle,
  title,
  description,
  onClose,
  acceptLabel = 'Accept',
  onAcceptClick,
  acceptButtonColor = type === 'confirm' ? 'error' : 'secondary',
  loading = false,
  acceptDisabled = loading,
  withCancelButton = false,
  onCancelClick = onClose,
  cancelLabel = 'Cancel',
  cancelButtonColor = 'secondary',
  withoutTitleImage,
  children,
  modalFooter,
  isStreamEnding = false,
}) => {
  const classNames: ModalProps['classNames'] = {
    overlay: styles.overlay,
    modalContainer: styles.container,
    modal: cn(styles.modal, isStreamEnding && styles.modalStreamEnding),
    closeButton: styles.closeButton,
  };

  const img = notificationTypeIcons[type];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        onAcceptClick &&
        !loading &&
        !acceptDisabled
      ) {
        onAcceptClick();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onAcceptClick, loading, acceptDisabled]);

  return (
    <Modal classNames={classNames} open={open} onClose={onClose} center>
      <div
        className={cn(styles.modalContent, {
          [styles.modalContentStreamEnding]: isStreamEnding,
        })}
      >
        <>
          {!withoutTitleImage && (
            <div
              className={cn(styles.imageWrapper, {
                [styles.imageWrapperStreamEnding]: isStreamEnding,
              })}
            >
              <img
                width="140"
                height="140"
                src={img}
                className={styles.image}
                alt={`${type} message`}
                loading="lazy"
              />
            </div>
          )}
          <h3
            className={cn(styles.title, {
              [styles.titleWithoutImageAbove]: withoutTitleImage,
            })}
          >
            {highlightedTitle && (
              <span
                className={cn(styles.highlighted, { [styles[type]]: !!type })}
              >
                {highlightedTitle}
              </span>
            )}
            {title}
          </h3>
          {description && <p className={styles.description}>{description}</p>}
          {children && <div className={styles.childrenWrapper}>{children}</div>}
          <div
            className={cn(styles.actions, {
              [styles.actionsStreamEnding]: isStreamEnding,
            })}
          >
            {withCancelButton && (
              <Button
                variant={cancelButtonColor}
                className={cn(styles.button, {
                  [styles.buttonStreamEnding]: isStreamEnding,
                })}
                onClick={onCancelClick}
              >
                {cancelLabel}
              </Button>
            )}
            {Boolean(onAcceptClick) && (
              <Button
                variant={acceptButtonColor}
                className={cn(styles.button, {
                  [styles.buttonStreamEnding]: isStreamEnding,
                })}
                onClick={onAcceptClick}
                loading={loading}
                disabled={acceptDisabled || loading}
              >
                {acceptLabel}
              </Button>
            )}
          </div>
          {modalFooter}
        </>
      </div>
    </Modal>
  );
};

export default NotificationModal;
